import { useContext } from 'react'
import PropTypes from 'prop-types'

import { UpgradePanelContext } from './UpgradePanelContext'

import Badge from 'components/common/Badge'
import agent from 'agent'
import theme from 'theme'
import toast from 'lib/toast'
import OffCanvas, { offcanvasVariables } from 'components/common/OffCanvas'
import Icon from 'components/Icon'
import TextButton from 'components/common/TextButton'
import Button from 'components/common/Button'
import useAuth from 'hooks/use-auth'
import Text, { styles } from 'components/common/Text'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import { tiers } from 'utils/tiers'

const TierPanel = ({ tier }) => {
  const getBadgeGradient = () => {
    if (tier.title === 'Starter') {
      return [theme.colors.teal[0], theme.colors.blue[0]]
    } else if (tier.title === 'Pro') {
      return [theme.colors.blue[0], theme.colors.blue[0]]
    } else if (tier.title === 'Enterprise') {
      return [theme.colors.yellow[0], theme.colors.red[1]]
    }
  }

  return (
    <div
      css={{
        paddingLeft: theme.space[3],
        paddingRight: theme.space[3],
        paddingTop: theme.space[4],
        paddingBottom: theme.space[4],
        borderRadius: theme.borderRadius[0],
        background: theme.colors.whiteOverlay[0],
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gap: theme.space[2]
      }}
    >
      <header css={{ textAlign: 'center' }}>
        <Badge backgroundGradient={getBadgeGradient()}>{tier.title}</Badge>
        <Text
          as="h6"
          color="white"
          css={{
            lineHeight: 1,
            textTransform: 'uppercase',
            marginTop: theme.space[2]
          }}
        >
          {tier.sub}
        </Text>
        <Text as="p" color="white" css={{ marginTop: theme.space[3] }}>
          {tier.description}
        </Text>
      </header>
      <FeatureList>
        {tier.features.map(feature => (
          <FeatureListItem key={feature}>{feature}</FeatureListItem>
        ))}
      </FeatureList>
    </div>
  )
}

TierPanel.propTypes = {
  tier: PropTypes.object.isRequired
}

const FeatureList = props => (
  <ul
    css={{
      padding: 0,
      margin: 0,
      display: 'grid',
      listStyle: 'none'
    }}
    {...props}
  />
)

const FeatureListItem = ({ children, ...rest }) => {
  return (
    <li
      css={{
        // 1em used in calcs below is default size of checkmark
        borderTop: '1px solid',
        borderTopColor: theme.colors.steel[2],
        paddingTop: theme.space[2],
        paddingBottom: theme.space[2],
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: theme.space[3],
        paddingLeft: theme.space[2],

        '&:first-of-type': {
          borderTopWidth: 0
        }
      }}
      {...rest}
    >
      <Icon
        css={{
          color: theme.colors.white[0],
          position: 'relative',
          top: `calc((${styles.p.lineHeight * styles.p.fontSize}px - 1em) / 2)`
        }}
        icon="checkmark"
      />
      <Text as="p" color="white">
        {children}
      </Text>
    </li>
  )
}

FeatureListItem.propTypes = {
  children: PropTypes.node.isRequired
}

const UpgradePanel = () => {
  const {
    dispatch,
    state: { upgradePanelOpen, hasUpgradeBeenRequested }
  } = useContext(UpgradePanelContext)
  const { companyId } = useAuth()

  const panelWidth = `calc(${
    offcanvasVariables(theme).minWidth
  } - var(--nav-width))`
  const panelPadding = offcanvasVariables(theme).padding * 1.5 // This is that magical 48px we don't have available yet, but is at least based off of a theme var we use in the `<OffCanvas />` component

  const togglePanel = () => {
    dispatch({
      type: 'toggleUpgradePanel'
    })
  }

  const requestUpgrade = async () => {
    try {
      await agent.patch(`/companies/${companyId}/contracts/current`, {
        change_requested: 'Enterprise'
      })

      dispatch({
        type: 'setUpgradeHasBeenRequested'
      })

      toast({
        type: 'success',
        text: 'Upgrade has been requested.'
      })
    } catch (e) {
      toast({
        type: 'error',
        text: 'There was an error with your upgrade request.'
      })
    }
  }

  return (
    <OffCanvas
      data-test-upgrade-panel
      css={{
        left: 'var(--nav-width)', // Provides offset to dashboard navigation

        '.OffCanvas__content': {
          background: theme.colors.steel[2]
        },

        '.OffCanvas__body': {
          padding: panelPadding,
          paddingTop: 0
        }
      }}
      data-testid="data-test-upgrade-panel"
      minWidth={panelWidth}
      open={upgradePanelOpen}
      position="left"
      renderHeader={() => (
        <header
          css={{
            paddingLeft: panelPadding,
            paddingRight: panelPadding,
            paddingTop: offcanvasVariables(theme).padding,
            paddingBottom: 0
          }}
        >
          <TextButton
            white
            css={{
              marginLeft: 'auto'
            }}
            onClick={togglePanel}
          >
            <Icon
              aria-label="close"
              css={{
                fontSize: theme.fontSizes[3]
              }}
              icon="close"
            />
          </TextButton>
        </header>
      )}
      setIsOpen={togglePanel}
    >
      <Text as="h1" color="white">
        Your road to resilience starts at zero.
      </Text>
      <Text
        as="h3"
        color="light"
        css={{
          marginBottom: theme.space[3]
        }}
      >
        Upgrade to unlock more attacked targets
      </Text>
      <hr
        css={{
          borderTopColor: theme.colors.whiteOverlay[0],
          marginTop: theme.space[5],
          marginBottom: theme.space[5]
        }}
      />
      <div
        css={{
          display: 'grid',
          gap: theme.space[7],
          alignItems: 'center',
          gridTemplateColumns: '2fr 1.25fr'
        }}
      >
        <div>
          <Text
            as="h4"
            css={{
              color: theme.colors.white[0],
              marginBottom: theme.space[3]
            }}
          >
            Schedule an upgrade call:
          </Text>
          <Text
            as="p"
            color="white"
            css={{ marginTop: theme.space[3], marginBottom: theme.space[3] }}
          >
            Connect with a member of the Gremlin team to get more information
            about Gremlin’s pricing tiers, help us understand your reliability
            goals, and find the best option for your team.
          </Text>
        </div>
        <div css={{ textAlign: 'center' }}>
          <Button
            data-test-upgrade-panel-button
            stretch
            data-track-action="schedule-call"
            data-track-category="upgrade-panel"
            data-track-on="click"
            disabled={hasUpgradeBeenRequested}
            onClick={requestUpgrade}
          >
            {hasUpgradeBeenRequested ? 'Upgrade requested' : 'Schedule a call'}
          </Button>
          {hasUpgradeBeenRequested && (
            <div
              css={{
                marginTop: theme.space[3]
              }}
            >
              <Text as="small">
                We have received your request and will be in touch soon!
              </Text>
            </div>
          )}
        </div>
      </div>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: theme.space[2],
          marginTop: theme.space[5]
        }}
      >
        {tiers.map(tier => (
          <TierPanel key={tier.title} tier={tier} />
        ))}
      </div>

      <div css={{ marginTop: theme.space[3], textAlign: 'right' }}>
        <TrackedExternalLink
          css={{
            fontSize: styles.h5.fontSize,
            color: theme.colors.white[0],
            textTransform: 'uppercase',

            '&:hover, &:focus': {
              color: theme.colors.white[0]
            }
          }}
          href="https://www.gremlin.com/pricing?ref=webapp-upgrade-panel"
        >
          View the full feature list{' '}
          <Icon
            css={{
              marginLeft: theme.space[2],
              fontSize: theme.fontSizes[2]
            }}
            icon="chevronForward"
          />
        </TrackedExternalLink>
      </div>
    </OffCanvas>
  )
}

export default UpgradePanel
